/**
 * Limits Service
 * 
 * Handles reading and writing entity limits configuration from Firestore.
 * Limits are stored in config/limits document and control how many entities
 * free tier users can create.
 */

import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'

// Config collection and document
export const CONFIG_COLLECTION = 'config'
export const LIMITS_DOC = 'limits'

/**
 * Default limits for free tier users.
 * These are used if no config document exists.
 */
export const DEFAULT_LIMITS = {
  free: {
    projects: 1,        // Max projects for free users
    characters: 5,      // Per project
    locations: 5,
    events: 5,
    shots: 5,
    images: 10          // Total images per project
  },
  // Paid tiers have no limits (null = unlimited)
  subscription: null,
  project: null,        // Per purchased project - no limits
  lifetime: null
}

/**
 * Get the limits configuration document reference
 */
function getLimitsDocRef() {
  return doc(db, CONFIG_COLLECTION, LIMITS_DOC)
}

/**
 * Fetch current limits configuration from Firestore
 * Returns default limits if document doesn't exist
 * 
 * @returns {Promise<object>} Limits configuration
 */
export async function getLimitsConfig() {
  try {
    const docRef = getLimitsDocRef()
    const docSnap = await getDoc(docRef)
    
    if (docSnap.exists()) {
      return {
        ...DEFAULT_LIMITS,
        ...docSnap.data(),
        // Ensure free limits have all fields
        free: {
          ...DEFAULT_LIMITS.free,
          ...(docSnap.data().free || {})
        }
      }
    }
    
    return DEFAULT_LIMITS
  } catch (error) {
    console.error('[limits] Failed to fetch limits config:', error)
    return DEFAULT_LIMITS
  }
}

/**
 * Subscribe to limits configuration changes
 * 
 * @param {Function} callback - Called with limits config on changes
 * @returns {Function} Unsubscribe function
 */
export function subscribeLimitsConfig(callback) {
  const docRef = getLimitsDocRef()
  
  return onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        callback({
          ...DEFAULT_LIMITS,
          ...docSnap.data(),
          free: {
            ...DEFAULT_LIMITS.free,
            ...(docSnap.data().free || {})
          }
        })
      } else {
        callback(DEFAULT_LIMITS)
      }
    },
    (error) => {
      console.error('[limits] Error subscribing to limits config:', error)
      callback(DEFAULT_LIMITS)
    }
  )
}

/**
 * Update limits configuration (admin only)
 * 
 * @param {object} limits - New limits configuration
 * @returns {Promise<void>}
 */
export async function updateLimitsConfig(limits) {
  const docRef = getLimitsDocRef()
  await setDoc(docRef, limits, { merge: true })
}

/**
 * Initialize limits config document with defaults if it doesn't exist
 * Called from admin panel on first setup
 * 
 * @returns {Promise<void>}
 */
export async function initializeLimitsConfig() {
  const docRef = getLimitsDocRef()
  const docSnap = await getDoc(docRef)
  
  if (!docSnap.exists()) {
    await setDoc(docRef, DEFAULT_LIMITS)
    console.log('[limits] Initialized limits config with defaults')
  }
}

/**
 * Get limits for a specific plan
 * 
 * @param {object} config - Full limits config
 * @param {string} plan - User's plan ('free', 'subscription', 'project', 'lifetime')
 * @returns {object|null} Limits for that plan (null = unlimited)
 */
export function getLimitsForPlan(config, plan) {
  if (!config) return DEFAULT_LIMITS.free
  
  switch (plan) {
    case 'lifetime':
      return config.lifetime // null = unlimited
    case 'subscription':
      return config.subscription // null = unlimited
    case 'project':
      return config.project // null = unlimited for purchased projects
    case 'free':
    default:
      return config.free || DEFAULT_LIMITS.free
  }
}

/**
 * Check if a limit would be exceeded
 * 
 * @param {number|null} limit - The limit (null = unlimited)
 * @param {number} currentCount - Current count of entities
 * @returns {boolean} True if creating one more would exceed limit
 */
export function wouldExceedLimit(limit, currentCount) {
  if (limit === null || limit === undefined) {
    return false // No limit
  }
  return currentCount >= limit
}

/**
 * Get remaining count before limit
 * 
 * @param {number|null} limit - The limit (null = unlimited)
 * @param {number} currentCount - Current count
 * @returns {number|null} Remaining count (null = unlimited)
 */
export function getRemainingCount(limit, currentCount) {
  if (limit === null || limit === undefined) {
    return null // Unlimited
  }
  return Math.max(0, limit - currentCount)
}
